import {
  ClockCircleOutlined,
  FilterFilled,
  GoldOutlined
} from "@ant-design/icons";
import {
  Button,
  Input,
  InputNumber,
  message,
  Pagination,
  Popover,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import moment from "moment";
import { useEffect, useRef, useState } from "react";

import {
  editCartItemDetails,
  getCartItemDetails,
  getCartItemDetailsPredicted,
  getDailyLeadTimeMetrics,
} from "../../apis/procurement.api";

import { fetchVendors } from "../../apis/vendor.api";
import { ProcurementItemForm } from "../../components/procurement/form";
import { usePermissions } from "../../custom_hooks/permission/permission_hook";
// Shared with /oms/availability so both desks render stock identically.
import { VendorInventoryRows } from "../../components/inventory/vendor_inventory_rows";

export function ProcurementCartPage() {
  const { hasPermission } = usePermissions();
  const [page, setPage] = useState(1);
  const [cartItems, setCartItems] = useState<Array<any>>([]);
  const [vendors, setVendors] = useState<Array<any>>([]);
  const [search, setSearch] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [total_cart_items, setTotalCartItems] = useState<number>(0);
  const [lead_time, setLeadTime] = useState<string>("not_filled");
  const [city, setCity] = useState<string>("All");
  const [selectedVendor, setSelectedVendor] = useState<string>("All");
  // Server-side: lines whose variant is / is not on the sellable (auto-fill) list.
  const [autoFill, setAutoFill] = useState<"all" | "allowed" | "blocked">("all");
  const [metricsData, setMetricsData] = useState<any>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<Array<number>>([]);
  // Incoming stock-PO line picked per cart row (mirror row id). Seeded from the server's
  // `incoming_po_item` on load; the panel's "Use this PO" button changes it and the form's hidden
  // field is kept in step so the next save sends it.
  const [pickedPo, setPickedPo] = useState<Record<number, number | null>>({});
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("all");
  const [metricsLoading, setMetricsLoading] = useState(false);
  // Applied quantity filter (sent to the API). null value => no filter.
  const [quantityFilter, setQuantityFilter] = useState<{
    operator: "gt" | "lt";
    value: number | null;
  }>({ operator: "gt", value: null });
  // Draft edited inside the dropdown before the user clicks "Filter".
  const [quantityFilterDraft, setQuantityFilterDraft] = useState<{
    operator: "gt" | "lt";
    value: number | null;
  }>({ operator: "gt", value: null });
  const procurementFormRef = useRef<any>(null);

  const vendorSelectRef = useRef<HTMLDivElement>(null);

  const warehouseSelectRef = useRef<HTMLDivElement>(null);

  const formRefs = useRef<Record<number, any>>({});

  const getVendors = async () => {
    const vendors = await fetchVendors({});
    setVendors(vendors);
  };

  const fetchMetrics = async () => {
    setMetricsLoading(true);
    const data = await getDailyLeadTimeMetrics();
    setMetricsData(data);
    setMetricsLoading(false);
  };

  const fetchCartItems = async (params: any) => {
    setLoading(true);
    setPage(params?.page ?? 1);

    const q = {
      search: search,
      page: params?.page ?? 1,
    } as Record<string, any>;

    // Quantity filter (server-side: greater-than / less-than across all pages)
    if (quantityFilter.value !== null) {
      if (quantityFilter.operator === "gt") {
        q.quantity_gt = quantityFilter.value;
      } else {
        q.quantity_lt = quantityFilter.value;
      }
    }

    // Use predicted API if "predicted" tab is selected
    if (lead_time === "predicted") {
      // Add city filter if not "All"
      if (params?.city && params?.city !== "All") {
        q.city = params.city;
      }

      // Add vendor filter if not "All"
      if (params?.vendor && params?.vendor !== "All") {
        q.vendor = params.vendor;
      }

      if (autoFill !== "all") q.auto_fill = autoFill;
      const res: any = await getCartItemDetailsPredicted(q);
      setLoading(false);
      setCartItems(res?.results);
      setTotalCartItems(res?.count);
      return;
    }

    if (lead_time === "lead_time_but_not_warehouse") {
      console.log("leat_time_but_not_warehouse");
      q.lead_time_but_not_warehouse = "not_filled";
    } else if (lead_time === "not_filled") {
      console.log("not_filled");
      q.lead_time = "not_filled";
    } else if (lead_time === "") {
      console.log("all - no lead_time filter");
      // Don't add any lead_time related filters when "All" is selected
    }

    // Add city filter if not "All"
    if (params?.city && params?.city !== "All") {
      q.city = params.city;
    }

    // Add vendor filter if not "All"
    if (params?.vendor && params?.vendor !== "All") {
      q.vendor = params.vendor;
    }

    if (autoFill !== "all") q.auto_fill = autoFill;
    const res: any = await getCartItemDetails(q);

    console.log("res",res);

      if (lead_time == "not_filled" && search != "") {
        const allRes: any = await getCartItemDetails(q);
        console.log('allRes',allRes);
        const results = Array.isArray(allRes?.results) ? allRes.results : [];
        console.log("results",results);

        // 🔹 Filter for pending items only (lead_time == null or empty)
        const pendingOnly = results.filter(
          (r: any) =>
            r?.lead_time === null ||
            r?.lead_time === "" ||
            r?.lead_time === undefined
        );
        console.log("pendingOnly",pendingOnly);
        setLoading(false);
        setCartItems(pendingOnly);
        setTotalCartItems(allRes?.count);
        return;
      }

    setLoading(false);
    setCartItems(res?.results);
    setTotalCartItems(res?.count);
  };

  console.log("cartItems --> ", cartItems);

  const editCartItem = async (id: any, values: any) => {
    if (!hasPermission("procurement.carts")) {
      message.error("You don't have permission to edit cart items");
      return;
    }
    setLoading(true);

    // Do not allow quantity larger than the original quantity
    const originalQuantity = Number(
      cartItems.find((item) => item.id === id)?.cart?.quantity
    );
    if (Number(values?.quantity) > originalQuantity) {
      message.error("Quantity cannot be larger than the original quantity");
      setLoading(false);
      return;
    }

    const payload = {
      id: id,
      cart: values?.cart_id,
      lead_time: values?.lead_time,
      remark: values?.remarks,
      selected_vendor_ware_house: values?.selected_vendor_ware_house,
      selected_vendor: values?.selected_vendor,
      quantity: values?.quantity,
      // Optional stock-PO line; null clears it. Falls back to the row's current pick when the
      // caller built `values` by hand (the Available button).
      incoming_po_item:
        values?.incoming_po_item !== undefined ? values.incoming_po_item : pickedPo[id] ?? null,
      ...(lead_time === "predicted" && { from_prediction: true }),
    };

    console.log("payload", payload);

    const result: any = await editCartItemDetails(payload);

    setLoading(false);

    fetchCartItems({ page, search, city, vendor: selectedVendor });

    // The endpoint answers 201 even when it refuses the body; the refusal rides in the payload
    // as `status: 400` + `error` (e.g. a stock PO line for another product). Say so instead of
    // claiming success.
    if (result && (result.status === 400 || result.error)) {
      message.error(result.error || "Cart item could not be updated");
      return;
    }

    message.success("Cart Item updated successfully");
  };

  const columns = [
    {
      key: "cart_number",
      title: "Cart Number",
      render: (item: any) => {
        const vendorData = item?.cart?.variant?.vendor_data;
        const onlyOneVendor = vendorData?.length === 1;
        const onlyOneWarehouse = vendorData?.[0]?.warehouses?.length === 1;

        return (
          <div style={{ width: 190 }}>
            {item?.cart_number}

            <Space className="ms-2" size="small">
              {item?.cart?.variant?.is_sample && (
                <Tag
                  color="green"
                  style={{
                    fontSize: 10,
                  }}
                >
                  Sample
                </Tag>
              )}
              {item?.zone && (
                <Tag
                  color="blue"
                  style={{
                    fontSize: 10,
                  }}
                >
                  {item.zone}
                </Tag>
              )}
              <Button
                onClick={async () => {
                  try {
                    // Copy text to clipboard
                    var product_name =
                      item?.cart?.variant?.private_label_product_name ??
                      item?.cart?.variant?.product_name;
                    var quantity = item?.cart?.quantity ?? "";
                    var price_unit = item?.cart?.variant?.price_unit ?? "";

                    await navigator.clipboard.writeText(
                      product_name +
                        " \nQuantity:" +
                        quantity +
                        " " +
                        price_unit
                    );

                    message.success("Cart item copied");
                  } catch (err) {
                    console.error("Failed to copy text:", err);
                  }
                }}
                type="default"
                size="small"
              >
                Copy
              </Button>
            </Space>

            <Button
              onClick={async () => {
                formRefs.current[item.id]?.setFieldsValue?.({ lead_time: 0 });
                formRefs.current[item.id]?.setLeadTime?.(0);
                formRefs.current[item.id]?.setQuantity?.(item?.cart?.quantity);

                let formValues = formRefs.current[item.id]?.getFieldsValue?.();
                const vendorData = item?.cart?.variant?.vendor_data;
                const onlyOneVendor = vendorData?.length === 1;
                const onlyOneWarehouse =
                  vendorData?.[0]?.warehouses?.length === 1;

                console.log("vendorData --> ", vendorData);

                if (
                  formValues &&
                  formValues.selected_vendor &&
                  formValues.selected_vendor_ware_house
                ) {

                  const values = {
                    cart_id: item?.cart?.id,
                    lead_time: 0,
                    remarks: formValues.remarks ?? item?.remarks,
                    selected_vendor: formValues.selected_vendor,
                    selected_vendor_ware_house: formValues.selected_vendor_ware_house,
                    quantity: parseInt(formValues.quantity ?? item?.cart?.quantity,10),
                    incoming_po_item: formValues.incoming_po_item ?? null,
                  };
                  
                  await editCartItem(item?.id, values);
                  message.success("Cart updated with selected vendor and warehouse!");
                  return;
                }

                // Case 1: Only one vendor and one warehouse
                if (onlyOneVendor && onlyOneWarehouse) {
                  const values = {
                    cart_id: item?.cart?.id,
                    lead_time: 0,
                    remarks: item?.remarks,
                    selected_vendor: vendorData[0]?.id,
                    selected_vendor_ware_house:
                      vendorData[0]?.warehouses[0]?.id,
                    quantity: parseInt(item?.cart?.quantity, 10),
                  };
                  await editCartItem(item?.id, values);
                  message.success("Cart updated automatically!");
                  return;
                }

                // Case 2: Only one vendor, multiple warehouses
                if (onlyOneVendor) {
                  message.info("Please select a warehouse.");
                  setTimeout(() => {
                    warehouseSelectRef.current?.scrollIntoView({
                      behavior: "smooth",
                      inline: "center",
                    });
                  }, 100);
                  return;
                }

                // Case 3: Multiple vendors, no warehouse selected
                if (!formValues?.selected_vendor && vendorSelectRef.current) {
                  formRefs.current[item.id]?.openVendorDropdown?.();
                  message.info("Please select a vendor.");
                  setTimeout(() => {
                    vendorSelectRef.current?.scrollIntoView({
                      behavior: "smooth",
                      inline: "center",
                    });
                  }, 100);
                  return;
                }

                // Default: Ask user to select both
                message.info("Please select vendor and warehouse.");
              }}
              className="ms-2"
              type="primary"
              size="small"
            >
              Available
            </Button>

            {/* <div
              style={{
                fontSize: 10,
                color: "gray",
              }}
              className="mt-1"
            >
              Pending since {dayjs(item?.cart?.modified_at).fromNow()}
            </div> */}
          </div>
        );
      },
    },
    {
      key: "product_name",
      title: "Product Name",
      render: (item: any) => {
        return (
          <div style={{ width: 280 }}>
            {item?.cart?.variant?.private_label_product_name ??
              item?.cart?.variant?.product_name}
          </div>
        );
      },
    },

    {
      key: "quantity",
      title: "Quantity",
      filterIcon: () => (
        <FilterFilled
          style={{
            color: quantityFilter.value !== null ? "#24AAB7" : undefined,
          }}
        />
      ),
      filterDropdown: ({
        confirm,
      }: {
        confirm: () => void;
      }) => {
        const applyQuantityFilter = () => {
          // Push the draft to the applied filter -> triggers a server-side refetch.
          setQuantityFilter(quantityFilterDraft);
          confirm();
        };
        return (
          <div
            style={{ padding: 8, width: 220 }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Radio.Group
              value={quantityFilterDraft.operator}
              onChange={(e) =>
                setQuantityFilterDraft((prev) => ({
                  ...prev,
                  operator: e.target.value,
                }))
              }
              style={{ marginBottom: 8 }}
            >
              <Radio value="gt">Greater than</Radio>
              <Radio value="lt">Less than</Radio>
            </Radio.Group>
            <InputNumber
              style={{ width: "100%", marginBottom: 8 }}
              placeholder="Enter quantity"
              min={0}
              step={1}
              precision={0}
              value={quantityFilterDraft.value}
              onChange={(value) =>
                setQuantityFilterDraft((prev) => ({
                  ...prev,
                  value:
                    value === null || value === undefined
                      ? null
                      : Math.trunc(Number(value)),
                }))
              }
              onPressEnter={applyQuantityFilter}
            />
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={applyQuantityFilter}
                style={{ width: 90 }}
              >
                Filter
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setQuantityFilterDraft({ operator: "gt", value: null });
                  setQuantityFilter({ operator: "gt", value: null });
                  confirm();
                }}
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        );
      },
      render: (item: any) => {
        return <div style={{ width: 50 }}>{item?.cart?.quantity}</div>;
      },
    },

    {
      key: "edit",
      title: "Actions",
      render: (item: any) => {

        const vendorData = item?.cart?.variant?.vendor_data;

        const preSelectedVendor = vendorData?.[0];

        // Get warehouse id: if only one warehouse, use its id, else fallback
        let selectedWarehouseId = null;

        if (vendorData?.length === 1 && vendorData[0]?.warehouses?.length === 1) {
          selectedWarehouseId = vendorData[0].warehouses[0]?.id; // most time null
        } else { 
          selectedWarehouseId = item?.selected_vendor_ware_house?.id ?? item?.selected_vendor_ware_house; //most time not null
        }
         
        console.log("item --> ", item);

        const initData: Record<any, any> = {
          lead_time: item?.lead_time,
          predicted_lead_time: item?.predicted_lead_time,
          selected_vendor_id: item?.selected_vendor ?? preSelectedVendor?.id,
          //selected_vendor_id: item?.selected_vendor?.id ??  item?.selected_vendor,
          selected_vendor_ware_house_id: selectedWarehouseId,
          remarks: item?.remark,
          quantity: item?.quantity,
          predicted_quantity: item?.predicted_quantity,
          incoming_po_item: pickedPo[item.id] ?? item?.incoming_po_item ?? null,
        };

        return (
          <div>
            <ProcurementItemForm
              ref={(ref) => {
                formRefs.current[item.id] = ref;
              }}
              warehouseSelectRef={warehouseSelectRef}
              vendorSelectRef={vendorSelectRef}
              onSubmit={(values) => {
                editCartItem(item?.id, values);
              }}
              vendors={vendors}
              knownVendors={vendorData}
              // The row's open stock-PO lines → an "Incoming PO" dropdown in the form. The panel's
              // "Use this PO" button writes the same field, and both keep `pickedPo` in step.
              incomingPos={item?.incoming_purchase_orders ?? []}
              onIncomingPoChange={(id) => setPickedPo((p) => ({ ...p, [item.id]: id }))}
              initData={preSelectedVendor ? initData : {}}
              key={item?.id}
              context="cart"
              useQuantityField
              originalQuantity={item?.cart?.quantity}
            />
          </div>
        );
      },
    },

    {
      key: "public_product_name",
      title: "Public Product Name",
      render: (item: any) => {
        return (
          <div style={{ width: 340 }}>{item?.cart?.variant?.product_name}</div>
        );
      },
    },

    ...(lead_time === "predicted" ? [{
      key: "prediction_reasoning",
      title: "Reason",
      render: (item: any) => {
        return (
          <div style={{ width: 300 }}>
            {item?.prediction_reasoning || "-"}
          </div>
        );
      },
    }] : []),
  ];

  useEffect(() => {
    fetchCartItems({ page, search, lead_time, city, vendor: selectedVendor });
  }, [search, lead_time, city, selectedVendor, quantityFilter, autoFill]);

  // Seed each row's PO pick from the server so the panel shows what was saved.
  useEffect(() => {
    setPickedPo(
      Object.fromEntries(
        (cartItems ?? []).map((item: any) => [item.id, item?.incoming_po_item ?? null])
      )
    );
  }, [cartItems]);

  // Auto-expand rows that have stock held OR on order, so warehouse/qty/batch
  // and the incoming PO lines show below the row without a click.
  useEffect(() => {
    setExpandedRowKeys(
      (cartItems ?? [])
        .filter(
          (item: any) =>
            (item?.vendor_inventory?.length ?? 0) > 0 ||
            (item?.incoming_purchase_orders?.length ?? 0) > 0
        )
        .map((item: any) => item.id)
    );
  }, [cartItems]);

  useEffect(() => {
    getVendors();
  }, []);

  const getUniqueTimeSlots = () => {
    if (!metricsData?.hourly_metrics) return [];
    const slotsWithTime: Array<{ slot: string; time: moment.Moment }> = [];
    metricsData.hourly_metrics.forEach((metric: any) => {
      const startTime = moment(metric.start_time);
      // Only include slots at or after 9:00 AM IST
      if (startTime.hour() >= 9) {
        const startTimeStr = startTime.format("hh:mm A");
        const endTimeStr = moment(metric.end_time).format("hh:mm A");
        const slotStr = `${startTimeStr} - ${endTimeStr}`;
        if (!slotsWithTime.find(s => s.slot === slotStr)) {
          slotsWithTime.push({ slot: slotStr, time: startTime });
        }
      }
    });
    // Sort by actual time, not string
    slotsWithTime.sort((a, b) => a.time.valueOf() - b.time.valueOf());
    return slotsWithTime.map(s => s.slot);
  };

  const getFilteredMetrics = () => {
    if (!metricsData?.hourly_metrics) return [];

    return metricsData.hourly_metrics.filter((metric: any) => {
      const startTime = moment(metric.start_time);
      // Filter out entries before 9:00 AM IST
      if (startTime.hour() < 9) {
        return false;
      }
      const startTimeStr = startTime.format("hh:mm A");
      const endTimeStr = moment(metric.end_time).format("hh:mm A");
      return `${startTimeStr} - ${endTimeStr}` === selectedTimeSlot;
    });
  };

  const renderMetricsContent = () => {
    if (metricsLoading) {
      return <div className="p-3">Loading metrics...</div>;
    }

    if (!metricsData) {
      return <div className="p-3">No data available</div>;
    }

    const timeSlots = getUniqueTimeSlots();

    // Set first time slot as default if not already set
    if (timeSlots.length > 0 && selectedTimeSlot === "all") {
      setSelectedTimeSlot(timeSlots[0]);
    }

    const filteredMetrics = getFilteredMetrics();

    return (
      <div style={{ width: 500 }}>
        <div className="mb-3">
          <h6>Cart Update Metrics - {metricsData.date}</h6>
          <Select
            placeholder="Select Time Slot"
            style={{ width: "100%" }}
            value={selectedTimeSlot}
            onChange={setSelectedTimeSlot}
            options={timeSlots.map((slot) => ({
              value: slot,
              label: slot,
            }))}
          />
        </div>
        <div style={{ maxHeight: 400, overflowY: "auto" }}>
          <Table
            size="small"
            dataSource={filteredMetrics}
            pagination={false}
            columns={[
              {
                title: "User",
                dataIndex: "user_name",
                key: "user_name",
                width: 150,
              },
              {
                title: "Time Slot (IST)",
                key: "time_slot",
                width: 150,
                render: (record: any) =>
                  `${moment(record.start_time).format("hh:mm A")} - ${moment(
                    record.end_time
                  ).format("hh:mm A")}`,
              },
              {
                title: "Items Updated",
                dataIndex: "total_items_updated",
                key: "total_items_updated",
                width: 120,
                render: (value: number) => (
                  <span
                    style={{
                      color: value > 0 ? "green" : "gray",
                      fontWeight: value > 0 ? "bold" : "normal",
                    }}
                  >
                    {value}
                  </span>
                ),
              },
            ]}
          />
        </div>
        {metricsData.daily_totals && (
          <div className="mt-3 pt-3" style={{ borderTop: "1px solid #f0f0f0" }}>
            <h6>Daily Totals</h6>
            <Table
              size="small"
              dataSource={metricsData.daily_totals}
              pagination={false}
              columns={[
                {
                  title: "User",
                  key: "user_name",
                  render: (record: any) => {
                    const metric = metricsData.hourly_metrics.find(
                      (m: any) => m.user_id === record.user_id
                    );
                    return metric?.user_name || "Unknown";
                  },
                },
                {
                  title: "Total Items",
                  dataIndex: "total_items_updated",
                  key: "total_items_updated",
                  render: (value: number) => (
                    <span style={{ fontWeight: "bold", color: "#1890ff" }}>
                      {value}
                    </span>
                  ),
                },
              ]}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mx-3 my-3 px-3 py-2 white-color-bg rounded">
        <div className="d-flex justify-content-between align-items-center">
          <h3>Cart Availability Check</h3>
        <Popover
          content={renderMetricsContent()}
          title="Hourly Cart Update Metrics"
          trigger="click"
          placement="bottomRight"
          onOpenChange={(visible) => {
            if (visible && !metricsData) {
              fetchMetrics();
            }
          }}
        >
          <Button type="default">View Metrics</Button>
        </Popover>
      </div>

      <div className="d-flex justify-content-between">
        <Radio.Group
          value={lead_time}
          onChange={(e) => {
            setLeadTime(e.target.value);
          }}
          style={{ marginBottom: 16 }}
        >
          <Radio.Button value="not_filled">Pending</Radio.Button>

          <Radio.Button value="lead_time_but_not_warehouse">
            Vendor Warehouse Confirmation
          </Radio.Button>

          <Radio.Button value="">All</Radio.Button>

          <Radio.Button value="predicted">Predicted</Radio.Button>
        </Radio.Group>

        <div className="d-flex gap-2 mb-2">
          <Select
            style={{ width: 190 }}
            value={autoFill}
            onChange={(v) => setAutoFill(v)}
            options={[
              { value: "all", label: "Auto-fill: all products" },
              { value: "allowed", label: "Auto-fill allowed" },
              { value: "blocked", label: "Auto-fill off" },
            ]}
          />
          <Select
            placeholder="Select City"
            style={{ width: 150 }}
            value={city}
            onChange={(e) => {
              setCity(e);
            }}
            options={[
              { value: "All", label: "All" },
              { value: "Hyderabad", label: "Hyderabad" },
              { value: "Bangalore", label: "Bangalore" },
            ]}
          />
          <Select
            placeholder="Select Vendor"
            style={{ width: 300 }}
            value={selectedVendor}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) => {
              setSelectedVendor(value);
            }}
            optionLabelProp="label"
            options={[
              { value: "All", label: "All Vendors" },
              ...vendors.map((vendor) => ({
                value: vendor.id,
                label: vendor.name,
              })),
            ]}
            dropdownStyle={{ maxWidth: 400 }}
          />
          <Input.Search
            placeholder="Search"
            allowClear
            enterButton
            onSearch={(value) => {
              setLeadTime(value === "" ? "" : "not_filled");
              setSearch(value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={cartItems}
        pagination={false}
        loading={loading}
        scroll={{ x: 768 }}
        rowKey="id"
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) =>
            setExpandedRowKeys(keys as Array<number>),
          // Every line expands now: the panel carries the auto-fill toggle, which
          // matters most for a product we hold nothing of yet.
          rowExpandable: () => true,
          expandedRowRender: (item: any) => (
            <VendorInventoryRows
              rows={item?.vendor_inventory ?? []}
              incomingRows={item?.incoming_purchase_orders ?? []}
              showEmpty
              variantId={item?.cart?.variant?.id ?? item?.cart?.variant_id ?? null}
              selectedIncomingPoId={pickedPo[item.id] ?? null}
              onPickIncomingPo={(row) => {
                const id = row ? Number(row.id) : null;
                setPickedPo((p) => ({ ...p, [item.id]: id }));
                formRefs.current[item.id]?.setIncomingPoItem?.(id);
                message.info(
                  id
                    ? `${row.purchase_order_number || "PO"} selected · save the row to keep it`
                    : "PO cleared · save the row to keep it"
                );
              }}
            />
          ),
        }}
      />

      <Pagination
        current={page}
        total={total_cart_items ?? 1}
        showSizeChanger={false}
        pageSize={10}
        className="mt-3"
        onChange={(p) => {
          fetchCartItems({
            page: p,
            search,
            lead_time: lead_time,
            city: city,
            vendor: selectedVendor,
          });
        }}
      />
    </div>
  );
}
